import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AddressData, CreateUpdateAddressBody, AdminAddressApi } from 'api';
import { RootState } from 'app';
import { MAX_ADDRESS_AMOUNT } from 'utils/constants';

export const findAddressForCustomer = createAsyncThunk(
  'address/findAddressForCustomer',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, query } = params;
      const response = await AdminAddressApi.filter(id, query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findAddressForChainEntity = createAsyncThunk(
  'address/findAddressForChainEntity',
  async (params: { id: string }, { rejectWithValue }) => {
    try {
      const { id } = params;
      const response = await AdminAddressApi.all(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const filterAddress = createAsyncThunk(
  'address/filterAddress',
  async (params: { id: string; query: any }, { rejectWithValue }) => {
    try {
      const { id, query } = params;
      const response = await AdminAddressApi.filter(id, query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createAddressForEntity = createAsyncThunk<
  any,
  any,
  { rejectValue: { message: string } }
>(
  'address/createAddressForEntity',
  async (param: { id: string; body: any }, { rejectWithValue }) => {
    try {
      const { id, body } = param;
      const response = await AdminAddressApi.create(id, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createAddressForChainEntity = createAsyncThunk<
  any,
  any,
  { rejectValue: { message: string } }
>(
  'address/createAddressForChainEntity',
  async (data: { id: string; body: any }, { rejectWithValue }) => {
    try {
      const { id, body } = data;
      const response = await AdminAddressApi.create(id, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createAddressForCustomer = createAsyncThunk<
  any,
  {
    id: string;
    body: CreateUpdateAddressBody;
  },
  { rejectValue: { message: string } }
>(
  'address/createAddressForCustomer',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await AdminAddressApi.create(id, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateAddress = createAsyncThunk<
  any,
  { id: string; body: CreateUpdateAddressBody },
  { rejectValue: { message: string } }
>('address/updateAddress', async ({ id, body }, { rejectWithValue }) => {
  try {
    const response = await AdminAddressApi.update(id, body);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const updateAddressForCustomer = createAsyncThunk<
  any,
  { id: string; body: CreateUpdateAddressBody },
  { rejectValue: { message: string } }
>('address/updateAddress', async ({ id, body }, { rejectWithValue }) => {
  try {
    const response = await AdminAddressApi.update(id, body);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const removeAddress = createAsyncThunk<
  any,
  string,
  { rejectValue: { message: string } }
>('address/removeAddress', async (id, { rejectWithValue }) => {
  try {
    const response = await AdminAddressApi.delete(id);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const deleteAddress = createAsyncThunk<
  any,
  string,
  { rejectValue: { message: string } }
>('address/deleteAddress', async (id, { rejectWithValue }) => {
  try {
    const response = await AdminAddressApi.delete(id);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

interface AddressState {
  data: AddressData[];
  currentDeliveryAddress: CurrentDeliveryAddress | null;
  defaultDeliveryAddress: null | AddressData;
  isRequiredAddress: boolean;
  isMaxAddressAmount: boolean;
  isLoading: boolean;
  isLoadingCartModal: boolean;
}

export enum ADDRESS_TYPE {
  CUSTOMER = 1,
  PARTNER = 2,
  ANONYMOUS_ENTITY = 3,
  CHAIN_ENTITY = 4,
}

const initialState: AddressState = {
  data: [],
  currentDeliveryAddress: null,
  defaultDeliveryAddress: null,
  isRequiredAddress: false,
  isMaxAddressAmount: false,
  isLoading: true,
  isLoadingCartModal: true,
};

const addressSlice = createSlice({
  name: 'address',
  initialState,
  reducers: {
    updateIsLoadingCartModal(state, action) {
      state.isLoadingCartModal = action.payload;
    },
    addNewAddress(state, action: PayloadAction<any>) {
      if (action.payload.isSelected == true) {
        state.data = state.data.map(item => {
          return {
            ...item,
            isSelected: false,
          };
        });
      }

      state.data.splice(1, 0, action.payload);

      if (state.data.length >= MAX_ADDRESS_AMOUNT) {
        state.isMaxAddressAmount = true;
      } else {
        state.isMaxAddressAmount = false;
      }

      if (state.data.length == 1 && state.currentDeliveryAddress != null) {
        const currentDeliveryValue = {
          address: state.data[0].address,
          provinceCode: state?.data[0]?.ward?.provinceCode,
          districtCode: state.data[0].ward?.districtCode,
          wardCode: state.data[0]?.ward?.code,
          id: state.data[0].id,
          latitude: state.data[0].latitude,
          longitude: state.data[0].longitude,
          isSelected: true,
          isHome: state.data[0].isHome,
        };

        // @ts-ignore
        state.currentDeliveryAddress = currentDeliveryValue;
        localStorage.setItem(
          'DELIVERY_ADDRESS',
          JSON.stringify(currentDeliveryValue)
        );
      }
    },
    updateAddressInState(state, action: PayloadAction<any>) {
      const index = state.data.findIndex(item => item.id === action.payload.id);

      if (action.payload.isSelected == true) {
        state.data = state.data.map(item => {
          return {
            ...item,
            isSelected: false,
          };
        });
      }

      if (index >= 0) {
        state.data[index] = action.payload;
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(deleteAddress.fulfilled, (state, action) => {
      const { data } = action.payload;
      state.data = state.data.filter(item => item.id !== data.id);

      if (data.length >= MAX_ADDRESS_AMOUNT) {
        state.isMaxAddressAmount = true;
      } else {
        state.isMaxAddressAmount = false;
      }
    });
  },
});

export type CurrentDeliveryAddress = {
  id?: string;
  provinceCode: string;
  districtCode: string;
  wardCode: string;
  address: string;
  latitude: string;
  longitude: string;
  wardName?: string;
  provinceName?: string;
  districtName?: string;
};

const { actions, reducer: addressReducer } = addressSlice;

export default addressReducer;

export const { addNewAddress, updateAddressInState, updateIsLoadingCartModal } =
  actions;

export const selectAddress = (state: RootState): AddressState =>
  state.address as AddressState;
